import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Clock, Ban } from 'lucide-react';
import { formatDate } from '~/utils';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';

type SuccessState = {
  status: 'success';
  orderId: string;
  purchaseOrderNumber: string | null;
  shippedDate: string;
  deliveryStartDate: string;
  deliveryEndDate: string;
};

type ErrorCode =
  | 'invalid'
  | 'expired'
  | 'already-confirmed'
  | 'already-used'
  | 'cancelled'
  | 'order-placed-emails-held'
  | 'unknown';

type ErrorState = {
  status: 'error';
  code: ErrorCode;
};

type PageState = { status: 'loading' } | SuccessState | ErrorState;

interface ConfirmOrderResponse {
  orderId: string;
  purchaseOrderNumber: string | null;
  shippedDate: string;
  deliveryStartDate: string;
  deliveryEndDate: string;
}

async function confirmOrderByToken(
  token: string
): Promise<ConfirmOrderResponse> {
  const res = await fetch('/api/orders/confirm-by-token', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (res.ok) {
    return res.json() as Promise<ConfirmOrderResponse>;
  }

  const body: { error?: string; code?: string } = await res
    .json()
    .catch(() => ({}));

  if (res.status === 404) throw new TokenError('invalid');
  if (res.status === 410) throw new TokenError('expired');
  if (res.status === 409 && body.code === 'cancelled')
    throw new TokenError('cancelled');
  if (res.status === 409 && body.code === 'already-used')
    throw new TokenError('already-used');
  if (res.status === 409 && body.code === 'order-placed-emails-held')
    throw new TokenError('order-placed-emails-held');
  if (res.status === 409) throw new TokenError('already-confirmed');
  throw new TokenError('unknown');
}

class TokenError extends Error {
  code: ErrorCode;
  constructor(code: ErrorCode) {
    super(code);
    this.code = code;
  }
}

export function ConfirmOrder() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('confirm-order');
  const token = searchParams.get('token');
  const [state, setState] = useState<PageState>(
    token ? { status: 'loading' } : { status: 'error', code: 'invalid' }
  );
  const requestedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    // StrictMode double-invokes effects in dev; the second mount must not re-POST
    // because the backend marks the token used on the first call (HTTP 409 on retry).
    if (requestedRef.current) return;
    requestedRef.current = true;

    confirmOrderByToken(token)
      .then((data) =>
        setState({
          status: 'success',
          orderId: data.orderId,
          purchaseOrderNumber: data.purchaseOrderNumber,
          shippedDate: data.shippedDate,
          deliveryStartDate: data.deliveryStartDate,
          deliveryEndDate: data.deliveryEndDate,
        })
      )
      .catch((err: unknown) => {
        const code: ErrorCode =
          err instanceof TokenError ? err.code : 'unknown';
        setState({ status: 'error', code });
      });
  }, [token]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <PageLoadingSpinner name={t('confirm_order.loading')} />
      </div>
    );
  }

  if (state.status === 'error') {
    return <ErrorView code={state.code} />;
  }

  return <SuccessView {...state} />;
}

function SuccessView({
  orderId,
  purchaseOrderNumber,
  shippedDate,
  deliveryStartDate,
  deliveryEndDate,
}: Omit<SuccessState, 'status'>) {
  const { t } = useTranslation('confirm-order');
  const orderLabel = purchaseOrderNumber
    ? t('confirm_order.success.order_number', { number: purchaseOrderNumber })
    : t('confirm_order.success.order_number', { number: orderId });

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 p-4"
      data-testid="confirm-order-success"
    >
      <div className="bg-white rounded-xl shadow-md border border-gray-200 max-w-md w-full p-8">
        <div className="flex flex-col items-center gap-4 mb-6">
          <CheckCircle className="text-green-600" size={48} />
          <h1 className="text-xl font-bold text-gray-900">
            {t('confirm_order.success.title')}
          </h1>
          <p className="text-sm text-gray-500">{orderLabel}</p>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">
              {t('confirm_order.success.ship_date')}
            </span>
            <span className="font-medium text-gray-900" data-testid="ship-date">
              {formatDate(shippedDate)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">
              {t('confirm_order.success.delivery_window')}
            </span>
            <span
              className="font-medium text-gray-900"
              data-testid="delivery-window"
            >
              {formatDate(deliveryStartDate)} – {formatDate(deliveryEndDate)}
            </span>
          </div>
        </div>

        <Link
          to={`/orders/view/${orderId}`}
          className="block w-full text-center bg-primary text-white font-semibold py-3 px-6 rounded-lg hover:opacity-90 transition-opacity"
          data-testid="view-order-link"
        >
          {t('confirm_order.success.view_order')}
        </Link>
      </div>
    </div>
  );
}

const ERROR_CONFIG: Record<
  ErrorCode,
  {
    icon: React.ReactNode;
    titleKey: string;
    messageKey: string;
    ctaKey?: string;
    ctaHref?: string;
  }
> = {
  invalid: {
    icon: <XCircle className="text-red-500" size={48} />,
    titleKey: 'confirm_order.error.invalid.title',
    messageKey: 'confirm_order.error.invalid.message',
  },
  expired: {
    icon: <Clock className="text-amber-500" size={48} />,
    titleKey: 'confirm_order.error.expired.title',
    messageKey: 'confirm_order.error.expired.message',
    ctaKey: 'confirm_order.error.expired.cta',
    ctaHref: '/',
  },
  'already-confirmed': {
    icon: <CheckCircle className="text-blue-500" size={48} />,
    titleKey: 'confirm_order.error.already_confirmed.title',
    messageKey: 'confirm_order.error.already_confirmed.message',
  },
  'already-used': {
    icon: <CheckCircle className="text-blue-500" size={48} />,
    titleKey: 'confirm_order.error.already_used.title',
    messageKey: 'confirm_order.error.already_used.message',
  },
  cancelled: {
    icon: <Ban className="text-gray-400" size={48} />,
    titleKey: 'confirm_order.error.cancelled.title',
    messageKey: 'confirm_order.error.cancelled.message',
  },
  'order-placed-emails-held': {
    icon: <Clock className="text-warning-500" size={48} />,
    titleKey: 'confirm_order.error.order_placed_emails_held.title',
    messageKey: 'confirm_order.error.order_placed_emails_held.message',
  },
  unknown: {
    icon: <XCircle className="text-red-500" size={48} />,
    titleKey: 'confirm_order.error.unknown.title',
    messageKey: 'confirm_order.error.unknown.message',
    ctaKey: 'confirm_order.error.unknown.cta',
    ctaHref: '/',
  },
};

function ErrorView({ code }: { code: ErrorCode }) {
  const { t } = useTranslation('confirm-order');
  const config = ERROR_CONFIG[code];

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 p-4"
      data-testid="confirm-order-error"
    >
      <div className="bg-white rounded-xl shadow-md border border-gray-200 max-w-md w-full p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          {config.icon}
          <h1 className="text-xl font-bold text-gray-900">
            {t(config.titleKey)}
          </h1>
          <p className="text-sm text-gray-600">{t(config.messageKey)}</p>
          {config.ctaKey && config.ctaHref && (
            <Link
              to={config.ctaHref}
              className="mt-2 text-sm font-medium text-primary hover:underline"
              data-testid="error-cta-link"
            >
              {t(config.ctaKey)}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
